<template>
  <header class="header">
    <div class="header__logo">
      <h1>Booqimate</h1>
    </div>

    <div class="header__btns">
      <div class="d-none d-md-flex">
        <RouterLink :to="Routes.Admin" v-slot="{ navigate }">
          <VBtn
            icon="mdi-home"
            :ripple="false"
            variant="text"
            @click="navigate"
          />
        </RouterLink>

        <RouterLink :to="Routes.AdminCustomers" v-slot="{ navigate }">
          <VBtn
            :ripple="false"
            icon="mdi-account-group-outline"
            variant="text"
            @click="navigate"
          />
        </RouterLink>

        <RouterLink :to="Routes.AdminSchedule" v-slot="{ navigate }">
          <VBtn
            :ripple="false"
            icon="mdi-calendar-clock-outline"
            variant="text"
            @click="navigate"
          />
        </RouterLink>

        <RouterLink :to="Routes.AdminSettings" v-slot="{ navigate }">
          <VBtn
            icon="mdi-cog-outline"
            :ripple="false"
            variant="text"
            @click="navigate"
          />
        </RouterLink>

        <VMenu>
          <template #activator="{ props }">
            <VBtn
              icon="mdi-home-switch"
              :ripple="false"
              v-bind="props"
              variant="text"
            ></VBtn>
          </template>

          <VList
            @click:select="onCompanyChange"
            :selected="[companyStore.currentCompany]"
          >
            <VListItem
              v-for="item in companyStore.userCompanies"
              :key="item.id"
              :value="item"
            >
              <VListItemTitle>
                {{ item.title }}
              </VListItemTitle>
            </VListItem>
          </VList>
        </VMenu>

        <VMenu>
          <template #activator="{ props }">
            <VBtn
              icon="mdi-translate"
              :ripple="false"
              v-bind="props"
              variant="text"
            ></VBtn>
          </template>

          <VList @click:select="onLangChange" :selected="[currentLang]">
            <VListItem
              v-for="item in availableLangs"
              :key="item.value"
              :value="item.value"
            >
              <VListItemTitle>
                {{ item.title }}
              </VListItemTitle>
            </VListItem>
          </VList>
        </VMenu>

        <VBtn
          icon="mdi-exit-to-app"
          variant="text"
          class="header__btns-end"
          @click="logoutApp"
        />
      </div>
    </div>
  </header>

  <nav class="bottom-nav d-flex d-md-none">
    <RouterLink :to="Routes.Admin" custom v-slot="{ navigate, isExactActive }">
      <button
        type="button"
        class="bottom-nav__item"
        :class="{ 'bottom-nav__item--active': isExactActive }"
        @click="navigate"
      >
        <VIcon icon="mdi-home" />
        <span>{{ $t("home") }}</span>
      </button>
    </RouterLink>

    <RouterLink
      :to="Routes.AdminCustomers"
      custom
      v-slot="{ navigate, isExactActive }"
    >
      <button
        type="button"
        class="bottom-nav__item"
        :class="{ 'bottom-nav__item--active': isExactActive }"
        @click="navigate"
      >
        <VIcon icon="mdi-account-group-outline" />
        <span>{{ $t("customers") }}</span>
      </button>
    </RouterLink>

    <RouterLink
      :to="Routes.AdminSchedule"
      custom
      v-slot="{ navigate, isExactActive }"
    >
      <button
        type="button"
        class="bottom-nav__item"
        :class="{ 'bottom-nav__item--active': isExactActive }"
        @click="navigate"
      >
        <VIcon icon="mdi-calendar-clock-outline" />
        <span>{{ $t("reservationing") }}</span>
      </button>
    </RouterLink>

    <RouterLink
      :to="Routes.AdminSettings"
      custom
      v-slot="{ navigate, isExactActive }"
    >
      <button
        type="button"
        class="bottom-nav__item"
        :class="{ 'bottom-nav__item--active': isExactActive }"
        @click="navigate"
      >
        <VIcon icon="mdi-cog-outline" />
        <span>{{ $t("settings_nav") }}</span>
      </button>
    </RouterLink>

    <VMenu location="top" :close-on-content-click="false">
      <template #activator="{ props }">
        <button type="button" class="bottom-nav__item" v-bind="props">
          <VIcon icon="mdi-dots-horizontal" />
          <span>{{ $t("more") }}</span>
        </button>
      </template>

      <VList class="bottom-nav__more">
        <VMenu location="end">
          <template #activator="{ props }">
            <VListItem v-bind="props">
              <template #prepend>
                <VIcon>mdi-home-switch</VIcon>
              </template>
              <VListItemTitle>{{ $t("company") }}</VListItemTitle>
            </VListItem>
          </template>

          <VList
            @click:select="onCompanyChange"
            :selected="[companyStore.currentCompany]"
          >
            <VListItem
              v-for="item in companyStore.userCompanies"
              :key="item.id"
              :value="item"
            >
              <VListItemTitle>
                {{ item.title }}
              </VListItemTitle>
            </VListItem>
          </VList>
        </VMenu>

        <VMenu location="end">
          <template #activator="{ props }">
            <VListItem v-bind="props">
              <template #prepend>
                <VIcon>mdi-translate</VIcon>
              </template>
              <VListItemTitle>{{ $t("language") }}</VListItemTitle>
            </VListItem>
          </template>

          <VList @click:select="onLangChange" :selected="[currentLang]">
            <VListItem
              v-for="item in availableLangs"
              :key="item.value"
              :value="item.value"
            >
              <VListItemTitle>
                {{ item.title }}
              </VListItemTitle>
            </VListItem>
          </VList>
        </VMenu>

        <VListItem @click="logoutApp">
          <template #prepend>
            <VIcon>mdi-exit-to-app</VIcon>
          </template>
          <VListItemTitle>{{ $t("logout") }}</VListItemTitle>
        </VListItem>
      </VList>
    </VMenu>
  </nav>
</template>

<script setup lang="ts">
import { useRouter } from "vue-router";
import {
  getCompanyEmployees,
  getCompanyServices,
  getCurrentUser,
  getUserCompanies,
  logout,
} from "../services/api.service";
import { useCompanyStore } from "../stores/company.store";
import { Routes } from "../types/enums/routes.enum";
import { Company, CurrentCompany } from "../types/company.type";
import { onMounted, ref } from "vue";
import { useUserStore } from "../stores/user.store";
import { useI18n } from "vue-i18n";

const companyStore = useCompanyStore();
const userStore = useUserStore();
const router = useRouter();
const i18n = useI18n({
  useScope: "global",
});

const currentLang = ref(localStorage.getItem("lang") || "en");
const availableLangs = ref([
  { title: "English", value: "en" },
  { title: "Українська", value: "uk" },
]);

onMounted(async () => {
  const [companies, user] = await Promise.all([
    getUserCompanies(),
    getCurrentUser(),
  ]);
  if (currentLang) {
    i18n.locale.value = currentLang.value;
  }

  if (companies.length) {
    companyStore.userCompanies = companies;
    userStore.user = user;
    userStore.subscription = user.subscription;
    const currentCompany = companies.find(
      (company) => company.id == companyStore.currentCompany?.id,
    );
    if (currentCompany) {
      companyStore.currentCompany = currentCompany as CurrentCompany;
      const employees = await getCompanyEmployees(
        companyStore.currentCompany?.id!,
      );
      companyStore.employees = employees;
    }
  } else {
    await logout();
  }
});

const logoutApp = async () => {
  await logout();
  companyStore.$reset();
  userStore.$reset();
  router.push(Routes.Main);
};

const onLangChange = (event: { id: unknown }) => {
  const lang = event.id as string;
  i18n.locale.value = lang;
  localStorage.setItem("lang", lang);
  currentLang.value = lang;
};

const onCompanyChange = async (event: {
  id: unknown;
  value: boolean;
  path: unknown[];
}) => {
  const company = event.id as Company;
  const [employees, services] = await Promise.all([
    getCompanyEmployees(company.id!),
    getCompanyServices(company.id!),
  ]);

  companyStore.$patch({
    currentCompany: company,
    employees: employees,
    services: services,
  });
};
</script>

<style lang="scss" scoped>
.header {
  border-radius: 0px 0px 20px 20px;
  background: #fff;
  width: 100%;
  // height: 70px;
  padding: 8px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  margin-bottom: 33px;

  &__logo {
    max-width: 300px;

    h1 {
      color: #504747;

      text-align: center;
      font-family: Montserrat;
      font-size: 25px;
      font-style: normal;
      font-weight: 700;
    }
  }

  &__btns {
    display: flex;
    column-gap: 36px;

    :deep(.v-icon) {
      font-size: 30px;
      color: black;
    }

    &-end {
      margin-left: 30px;
    }
  }
}

.bottom-nav {
  position: fixed;
  // Explicit inset margins instead of a `left:50%` + transform centering
  // trick - some long Ukrainian labels below ("Налаштування",
  // "Бронювання") are single unbreakable words wide enough that this bar's
  // natural content width equals the *entire* viewport, so a shrink-to-fit
  // auto width just reproduced that same full width instead of centering
  // a smaller pill. Pinning both edges instead gives it an unambiguous,
  // predictable width up front, and the items below share that width
  // equally and wrap their label text rather than force an overflow.
  left: 12px;
  right: 12px;
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  z-index: 100;

  align-items: center;
  justify-content: space-between;
  column-gap: 4px;
  padding: 8px 12px;

  background: #fff;
  border-radius: 20px;
  box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.15);

  &__item {
    display: flex;
    flex: 1 1 0;
    min-width: 0;
    flex-direction: column;
    align-items: center;
    row-gap: 2px;
    padding: 6px 4px;
    border: none;
    background: transparent;
    border-radius: 14px;
    color: #504747;
    cursor: pointer;
    transition:
      background-color 0.15s ease,
      color 0.15s ease;

    .v-icon {
      font-size: 22px;
    }

    span {
      display: block;
      max-width: 100%;
      overflow-wrap: anywhere;
      text-align: center;
      font-family: Montserrat, sans-serif;
      font-size: 10px;
      font-weight: 500;
      line-height: 12px;
    }

    &--active {
      background: #403838;
      color: #fff;
    }
  }
}

.bottom-nav__more {
  min-width: 220px;
  border-radius: 18px;
  box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.15);
  padding: 8px;
  overflow: hidden;

  :deep(.v-list-item) {
    border-radius: 12px;
    min-height: 44px;
  }

  :deep(.v-list-item .v-icon) {
    color: #403838;
  }
}
</style>
